import React from 'react';
import { useImageSlot, useGallerySlots, isVideoUrl } from '../lib/image-slots-client';
import { useEditMode } from '../contexts/EditModeContext';
import { Pencil } from '../lib/icons';
import MediaCarousel from './MediaCarousel';

const CONTAINER_ASPECT: Record<string, string> = {
  square:      'aspect-square',
  portrait:    'aspect-[3/4]',
  landscape:   'aspect-video',
  wide:        'aspect-[21/9]',
  hero:        'aspect-[16/5]',
  'tall-hero': 'aspect-[4/3] md:aspect-[16/9]',
};

export interface ImageSlotProps {
  slotId: string;
  fallbackSrc: string;
  fallbackAlt?: string;
  className?: string;
  wrapperClassName?: string;
  eager?: boolean;
}

export default function ImageSlot({
  slotId,
  fallbackSrc,
  fallbackAlt = '',
  className = '',
  wrapperClassName = '',
  eager = false,
}: ImageSlotProps) {
  const { slot } = useImageSlot(slotId);
  const { gallerySlots } = useGallerySlots(slotId);
  const editMode = useEditMode();

  const src       = slot?.image_url ?? fallbackSrc;
  const alt       = slot?.alt_text  || fallbackAlt;
  const focalX    = slot?.focal_x   ?? 50;
  const focalY    = slot?.focal_y   ?? 50;
  const zoom      = slot?.zoom      ?? 1;
  const objectFit = (slot?.object_fit ?? 'cover') as 'cover' | 'contain';
  const containerSizeKey = slot?.container_size ?? 'auto';

  const aspectClass = CONTAINER_ASPECT[containerSizeKey] ?? '';
  const wrapClass   = `relative ${aspectClass} ${wrapperClassName}`.trim();

  const primaryStyle: React.CSSProperties = {
    objectPosition: `${focalX}% ${focalY}%`,
    ...(zoom !== 1
      ? { transform: `scale(${zoom})`, transformOrigin: `${focalX}% ${focalY}%` }
      : {}),
  };

  // All carousel items: primary first, then gallery extras
  const allItems = [
    ...(src ? [{ url: src, alt }] : []),
    ...gallerySlots
      .filter(g => g.image_url)
      .map(g => ({ url: g.image_url!, alt: g.alt_text || alt })),
  ];

  const hasMedia  = allItems.length > 0;
  const hasGallery = gallerySlots.some(g => g.image_url);

  // ── Edit mode ────────────────────────────────────────────────────────────────
  if (editMode.active) {
    return (
      <div className={`${wrapClass} group/editslot`}>
        {/* Content */}
        {hasMedia ? (
          <MediaCarousel
            items={allItems}
            objectFit={objectFit}
            primaryStyle={primaryStyle}
            eager={eager}
            imgClassName={className}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/60">
            <div className="flex flex-col items-center gap-2 text-gray-600">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-8 w-8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 21h18M7.5 10.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
              </svg>
              <p className="text-xs">No image</p>
            </div>
          </div>
        )}

        {/* Always-visible edit ring */}
        <div className="absolute inset-0 ring-[3px] ring-[#39FF14]/50 ring-inset z-20 pointer-events-none transition-all duration-150 group-hover/editslot:ring-[#39FF14]" />

        {/* Edit button — always visible, brightens on hover */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editMode.onEditSlot(slotId, slot ?? null, fallbackSrc);
          }}
          className="pointer-events-auto absolute top-2 right-2 z-20 flex items-center gap-1.5 bg-[#39FF14] text-[#0D0D0D] text-xs font-bold px-2.5 py-1.5 rounded-lg shadow-[0_0_10px_rgba(57,255,20,0.5)] transition-all group-hover/editslot:shadow-[0_0_16px_rgba(57,255,20,0.8)]"
        >
          <Pencil className="h-3.5 w-3.5" />
          {src || hasGallery ? 'Edit' : 'Add'}
        </button>

        {/* Gallery count badge (only when multiple items) */}
        {hasGallery && (
          <div className="absolute bottom-2 left-2 bg-black/70 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full z-20 pointer-events-none">
            {allItems.length} photos
          </div>
        )}
      </div>
    );
  }

  // ── Public mode ──────────────────────────────────────────────────────────────
  if (!hasMedia) {
    return (
      <div className={`${wrapClass} flex items-center justify-center bg-gray-950`}>
        <div className="flex flex-col items-center gap-2 text-gray-600">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-8 w-8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 21h18M7.5 10.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
          </svg>
          <p className="text-xs">Photo coming soon</p>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapClass}>
      <MediaCarousel
        items={allItems}
        objectFit={objectFit}
        primaryStyle={primaryStyle}
        eager={eager}
        imgClassName={className}
      />
    </div>
  );
}
